import type { ElementType } from "react";
import styles from "./IconButton.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import { Button, type ButtonProps } from "@base-ui/react/button";
import { type Color } from "./Text";
import Tooltip from "./Tooltip";
import useCanWrite from "../../hooks/use-can-write";
import { READ_ONLY_MESSAGE } from "../../utils/permissions";

type IconButtonSize = "sm" | "md";

export type IconButtonVariant = "default" | "plain" | "primary";

const ICON_SIZE: Record<IconButtonSize, number> = { sm: 18, md: 24 };

interface IconButtonProps extends ButtonProps {
  icon: ElementType;
  label: string;
  color?: Color;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  active?: boolean;
  requiresWrite?: boolean;
  profileId?: number | null;
}

export default function IconButton({
  icon: Icon,
  label,
  color = "neutral",
  className,
  variant = "default",
  size = "md",
  active = false,
  requiresWrite,
  profileId,
  disabled,
  ...props
}: IconButtonProps) {
  const writable = useCanWrite(profileId);
  const blocked = requiresWrite === true && !writable;

  const button = (
    <Button
      className={classnames(
        styles.iconButton,
        styles[color],
        styles[`variant-${variant}`],
        styles[`size-${size}`],
        active && styles.active,
        className,
      )}
      aria-label={label}
      aria-pressed={active}
      title={label}
      disabled={blocked || disabled}
      {...props}
    >
      <Icon size={ICON_SIZE[size]} />
    </Button>
  );

  if (blocked) {
    return <Tooltip content={READ_ONLY_MESSAGE}>{button}</Tooltip>;
  }
  return button;
}
